import ChevronRightThin from '@assets/icons/chevron-right-thin.svg';
import React, { useEffect, useRef, useState } from 'react';
import { Animated, Easing, Pressable, View } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { Image } from 'expo-image';
import { SidebarNavItem } from '@experiences/components/sidebar-nav-item';
import { SidenavGroupItem, SidenavSingleItem } from '@/domain/navigation/types';
import { Typography } from '@usepepper/ui/atoms/typography';

const ANIMATION_DURATION = 200;
const ICON_SIZE = 16;

interface SidebarNavGroupProps {
  item: SidenavGroupItem;
  onSubnavItemPress: (item: SidenavSingleItem) => void;
}

export const SidebarNavGroup: React.FC<SidebarNavGroupProps> = ({
  item,
  onSubnavItemPress,
}) => {
  const [styles, theme] = useStyles();
  const { subnavItems } = item;
  const hasActiveSubnavItem = subnavItems.some((c) => c.active);
  const isHeaderActive = item.active || hasActiveSubnavItem;
  const [expanded, setExpanded] = useState(isHeaderActive);
  const [contentHeight, setContentHeight] = useState(0);
  const animatedValue = useRef(
    new Animated.Value(isHeaderActive ? 1 : 0),
  ).current;

  useEffect(() => {
    Animated.timing(animatedValue, {
      duration: ANIMATION_DURATION,
      easing: Easing.out(Easing.exp),
      toValue: expanded ? 1 : 0,
      useNativeDriver: false,
    }).start();
  }, [expanded, animatedValue]);

  useEffect(() => {
    if (hasActiveSubnavItem) {
      setExpanded(true);
    }
  }, [hasActiveSubnavItem]);

  const Icon = item.icon;
  const iconColor = theme.sk.color.icon.secondary;

  return (
    <View style={styles.group}>
      <Pressable
        accessibilityRole="button"
        onPress={() => setExpanded((prev) => !prev)}
        style={({ pressed }) => [
          styles.header,
          ...(isHeaderActive ? [styles.headerActive] : []),
          ...(pressed && !isHeaderActive ? [styles.headerPressed] : []),
        ]}
      >
        {Icon ? (
          <View style={styles.iconContainer}>
            <Icon
              color={iconColor}
              height={ICON_SIZE}
              stroke={iconColor}
              width={ICON_SIZE}
            />
          </View>
        ) : item.iconUrl ? (
          <Image
            alt={item.label}
            contentFit="contain"
            source={{ uri: item.iconUrl }}
            style={styles.iconContainer}
          />
        ) : null}
        <Typography
          color={
            isHeaderActive
              ? theme.sk.color.text.primary
              : theme.sk.color.text.secondary
          }
          variant={isHeaderActive ? 'paragraph-md-600' : 'paragraph-md'}
        >
          {item.label}
        </Typography>
        <Animated.View
          style={[
            styles.chevron,
            {
              transform: [
                {
                  rotate: animatedValue.interpolate({
                    inputRange: [0, 1],
                    outputRange: ['0deg', '90deg'],
                  }),
                },
              ],
            },
          ]}
        >
          <ChevronRightThin
            height={ICON_SIZE}
            stroke={iconColor}
            width={ICON_SIZE}
          />
        </Animated.View>
      </Pressable>
      <Animated.View
        style={[
          styles.subnav,
          {
            height: animatedValue.interpolate({
              inputRange: [0, 1],
              outputRange: [0, contentHeight],
            }),
            opacity: animatedValue,
          },
        ]}
      >
        <View
          onLayout={(e) => setContentHeight(e.nativeEvent.layout.height)}
          style={styles.subnavInner}
        >
          {subnavItems.map((subnavItem) => (
            <SidebarNavItem
              active={subnavItem.active}
              icon={subnavItem.icon}
              iconUrl={subnavItem.iconUrl}
              key={subnavItem.id ?? subnavItem.label}
              label={subnavItem.label}
              onPress={() => onSubnavItemPress(subnavItem)}
            />
          ))}
        </View>
      </Animated.View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  chevron: {
    alignItems: 'center',
    flexShrink: 0,
    justifyContent: 'center',
    marginLeft: 'auto',
  },
  group: {
    width: '100%',
  },
  header: {
    alignItems: 'center',
    borderRadius: theme.sk.radii.radiusSmall,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    height: theme.sk.spacing.spaceXlarge,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    width: '100%',
  },
  headerActive: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  headerPressed: {
    opacity: 0.7,
  },
  iconContainer: {
    flexShrink: 0,
    height: ICON_SIZE,
    width: ICON_SIZE,
  },
  subnav: {
    overflow: 'hidden',
  },
  subnavInner: {
    borderLeftColor: theme.sk.color.border.primary,
    borderLeftWidth: 1,
    left: theme.sk.spacing.spaceMedium,
    paddingLeft: theme.sk.spacing.spaceSmall,
    paddingTop: theme.sk.spacing.spaceXsmall,
    position: 'absolute',
    right: 0,
    top: 0,
  },
}));
